import React, { useCallback } from 'react';
import {
  Box,
  Typography,
  TextField,
  IconButton,
  Tooltip,
  Chip
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import { ExtendedQnaPair } from '../types';

interface QnaListItemProps {
  qna: ExtendedQnaPair;
  isTemp: boolean;
  isDeleting: boolean;
  isEditing: boolean;
  onEdit: (id: string) => void;
  onDelete: (id: string) => void;
  onSave: (id: string) => void;
  onCancel: (id: string) => void;
  onUpdateField: (id: string, field: 'question' | 'answer', value: string) => void;
  onTagClick: (tag: string) => void;
}

const QnaListItem: React.FC<QnaListItemProps> = React.memo(({
  qna,
  isTemp,
  isDeleting,
  isEditing,
  onEdit,
  onDelete,
  onSave,
  onCancel,
  onUpdateField,
  onTagClick
}) => {
  // Create stable handlers to prevent unnecessary re-renders
  const handleQuestionChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    onUpdateField(qna.qnaId, 'question', e.target.value);
  }, [qna.qnaId, onUpdateField]);

  const handleAnswerChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    onUpdateField(qna.qnaId, 'answer', e.target.value);
  }, [qna.qnaId, onUpdateField]);

  const handleEdit = useCallback(() => {
    onEdit(qna.qnaId);
  }, [qna.qnaId, onEdit]);

  const handleDelete = useCallback(() => {
    onDelete(qna.qnaId);
  }, [qna.qnaId, onDelete]);

  const handleSave = useCallback(() => {
    onSave(qna.qnaId);
  }, [qna.qnaId, onSave]);

  const handleCancel = useCallback(() => {
    onCancel(qna.qnaId);
  }, [qna.qnaId, onCancel]);
  return (
    <Box 
      sx={{ 
        mb: 1, 
        p: 1, 
        border: '1px solid #ccc', 
        borderRadius: 1,
        opacity: isTemp || isDeleting ? 0.7 : 1,
        bgcolor: isTemp ? '#f9f9f9' : isDeleting ? '#ffecec' : isEditing ? '#f0f7ff' : 'transparent',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        width: '100%',
        maxWidth: '100%',
        overflow: 'hidden'
      }}
    >
      <Box sx={{ flexGrow: 1, minWidth: 0, maxWidth: '100%' }}>
        {isEditing ? (
          <>
            <TextField
              fullWidth
              label="Question"
              value={qna.editedQuestion || qna.question}
              onChange={handleQuestionChange}
              size="small"
              margin="dense"
              variant="outlined"
            />
            <TextField
              fullWidth
              label="Answer"
              value={qna.editedAnswer || qna.answer}
              onChange={handleAnswerChange}
              size="small"
              margin="dense"
              variant="outlined"
              multiline
              rows={10}
            />
          </>
        ) : (
          <>
            <Typography variant="subtitle2" sx={{ wordBreak: 'break-word', overflowWrap: 'break-word' }}>
              Q: {qna.question.length > 150 ? qna.question.substring(0, 150) + '...' : qna.question}
            </Typography>
            <Typography variant="body2" sx={{ wordBreak: 'break-word', overflowWrap: 'break-word' }}>
              A: {qna.answer.length > 200 ? qna.answer.substring(0, 200) + '...' : qna.answer}
            </Typography>
            
            {/* Display the creation date, last updated date, imported date, and Q&A pair ID */}
            <Box sx={{ mt: 0.5 }}>
              {qna.createdAt && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  Created: {new Date(qna.createdAt).toLocaleString()}
                  {qna.importedDate && (
                    <span style={{ marginLeft: '8px', color: '#666' }}>
                      (Imported: {new Date(qna.importedDate).toLocaleString()})
                    </span>
                  )}
                </Typography>
              )}
              {qna.lastUpdated && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  Last Updated: {new Date(qna.lastUpdated).toLocaleString()}
                </Typography>
              )}
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontFamily: 'monospace', fontSize: '0.75rem' }}>
                ID: {qna.qnaId}
              </Typography>
            </Box>
            
            <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {qna.tags && qna.tags.length > 0 ? (
                qna.tags.map((tag, index) => (
                  <Chip 
                    key={index} 
                    label={tag} 
                    size="small" 
                    variant="outlined" 
                    icon={<LocalOfferIcon fontSize="small" />} 
                    onClick={() => onTagClick(tag)}
                  />
                ))
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
                  No tags
                </Typography>
              )}
            </Box>
          </>
        )}
        {isTemp && (
          <Typography variant="caption" color="text.secondary">
            Adding...
          </Typography>
        )}
        {isDeleting && (
          <Typography variant="caption" color="error">
            Deleting...
          </Typography>
        )}
      </Box>
      
      <Box sx={{ display: 'flex', alignItems: 'center', ml: 1 }}>
        {isEditing ? (
          <>
            <Tooltip title="Save">
              <IconButton 
                size="small" 
                color="primary"
                onClick={handleSave}
              >
                <SaveIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Cancel">
              <IconButton 
                size="small" 
                onClick={handleCancel}
              >
                <CancelIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </>
        ) : (
          <>
            <Tooltip title="Edit">
              <span>
                <IconButton 
                  size="small" 
                  color="primary"
                  onClick={handleEdit}
                  disabled={isTemp || isDeleting || isEditing}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            
            <Tooltip title="Delete">
              <span>
                <IconButton 
                  size="small" 
                  color="error"
                  onClick={handleDelete}
                  disabled={isTemp || isDeleting || isEditing}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </>
        )}
      </Box>
    </Box>
  );
});

// Add display name for debugging
QnaListItem.displayName = 'QnaListItem';

export default QnaListItem;